/**
 * The Alerts table's Where column: the device an alert is about, and the
 * floor it is on NOW (2026-10-20: device and current floor, UI only).
 *
 * An alert row names its subject (`sourceKind`, `sourceId`) and nothing
 * about place: `AlertOut` carries no level and neither does the `alerts`
 * table. So the floor is read from the console's own live state, through
 * the rule each other surface already reads it by, so that this column
 * never names a floor the Fleet, Devices or map screen would not:
 *
 *   robot  its PRESENCE (`robot-presence.ts`), worded as Fleet's Location
 *          column words it (`robot-lines.ts::locationText`) less the time,
 *          which the row's own Time column already carries: the floor
 *          while it is drawn there, "in lift SL1" while riding, "last
 *          recorded L9" for a ghost, and no floor when presence has no
 *          pose (lost its place, or lost with nothing recorded). A ghost
 *          is a robot nobody hears, a stub, or the (0, 0) LOST sentinel
 *          the adapter parks a robot at; its floor is where it WAS, so it
 *          is never shown as if it were current.
 *   lift   the car's floor only while this page trusts its reading
 *          (`LiftCarView.level`, the Devices table's rule), never the
 *          `currentLevel` a silent car's row still carries.
 *   door   its building-map level (`doors_registry`): a door does not
 *          move, and a live door's state row carries no level at all
 *          (`adaptV1DoorState`).
 *   task   the job's robot (the task list's `assignedRobot`, else the one
 *          the alert's own message names) and that robot's place, as
 *          above. Ops read the job's short id in front of it, since their
 *          click opens the job, not the robot; a site audience never reads
 *          a task id (`toast-words.ts`: a task id is not a hotel word).
 *          With no robot, the job alone ("Robot job" for the hotel).
 *
 * Every other kind is about no one place in the building -- a service on
 * the site box, a door adapter that drives several doors, the site's lift
 * control, the fire alarm -- and names only what it is about.
 */
import type { AlertViewModel } from '../../api/adapters';
import type { Audience } from '../../auth/roles';
import { alertSubject } from '../../state/attention/alert-subject';
import { TOAST_SOURCES } from '../../state/attention/toast-words';
import { splitRobotRef } from '../../state/locate';
import type { RobotPresence } from '../Map/robot-presence';

/** What a kind is called in front of its name. The device kinds use the
 *  Fleet and Devices tables' own headers (Robot, Door, Lift, Gateway). */
const KIND_LABEL: Readonly<Record<string, string>> = {
  robot: 'Robot',
  lift: 'Lift',
  door: 'Door',
  task: 'Task',
  gateway: 'Gateway',
  door_adapter: 'Door adapter',
  lift_write_gate: 'Lift control',
  site: 'Site',
};

/** Between the device and its floor: the separator `robot-lines` uses. */
const SEP = ' · ';

/** The live facts the column reads, looked up by the row's own ids.
 *  Null is "nobody can say": the device is named alone. */
export interface AlertWhereFacts {
  /** `robotWhereFloor` for the robot `ref` names (bare, or
   *  `fleet/robot`). */
  robotFloor(ref: string): string | null;
  /** The car's floor while its reading is trusted. */
  liftFloor(lift: string): string | null;
  /** The door's building-map level. */
  doorFloor(door: string): string | null;
  /** The robot the job is assigned to. */
  taskRobot(taskId: string): string | null;
}

/** The fields of a row the column reads. */
export type AlertWhereRow = Pick<
  AlertViewModel,
  'id' | 'severity' | 'message' | 'source' | 'rule' | 'sourceKind' | 'sourceId' | 'fingerprint'
>;

/**
 * A robot's place, from its presence: the floor, "in lift SL1", "last
 * recorded L9" / "last recorded in SL1" for a ghost, or null when there
 * is no pose to name (the robot is then named alone).
 */
export function robotWhereFloor(presence: RobotPresence | undefined): string | null {
  if (!presence) return null;
  // A rider's own pose names the floor it boarded on; the car is the place.
  if (presence.kind === 'riding' && presence.liftName) return `in lift ${presence.liftName}`;
  const pose = presence.pose;
  if (!pose || !pose.map) return null;
  if (pose.source !== 'live') {
    // A ghost: its last real pose. Said as the past, as Fleet says it.
    if (presence.kind === 'lost' && presence.liftName) return `last recorded in ${presence.liftName}`;
    return `last recorded ${pose.map}`;
  }
  // Live, quiet, checking or settling: drawn at its own record, on this
  // floor. Settling's "position updating" is about WHERE on the floor,
  // which this column does not say.
  return pose.map;
}

/**
 * A task id short enough for a cell, for ops: RMF's own ids are short
 * (`patrol.dispatch-48`) and pass whole; a long one keeps its head and its
 * tail, which is where two ids of one kind differ. The Message carries the
 * whole id.
 */
export function shortTaskId(id: string): string {
  return id.length <= 20 ? id : `${id.slice(0, 10)}…${id.slice(-6)}`;
}

function withFloor(device: string, floor: string | null): string {
  return floor ? `${device}${SEP}${floor}` : device;
}

/**
 * The Where text for one row.
 *
 * `subject` is what the row was called before this column existed (the
 * old Source column): the toast's and the bell's own subject line when the
 * words are translated for a site audience, the row's own source
 * otherwise. It is used where the row names no place: a legacy row with
 * no `sourceKind`, and, for a site audience, every kind that is not one
 * place, so the hotel reads "Site box", "Doors", "Lifts" or "Fire alarm",
 * never a service, adapter or site id.
 */
export function alertWhereText(
  row: AlertWhereRow,
  audience: Audience,
  subject: string,
  facts: AlertWhereFacts,
): string {
  const kind = row.sourceKind;
  const id = row.sourceId;
  if (!kind || !id) return subject;
  switch (kind) {
    case 'robot': {
      // Bare: robot rules once stamped `fleet/robot` (`alerts_engine.py`).
      const name = splitRobotRef(id)?.robotName ?? id;
      return withFloor(`${KIND_LABEL.robot} ${name}`, facts.robotFloor(id));
    }
    case 'lift':
      return withFloor(`${KIND_LABEL.lift} ${id}`, facts.liftFloor(id));
    case 'door':
      return withFloor(`${KIND_LABEL.door} ${id}`, facts.doorFloor(id));
    case 'task': {
      // The job's place is its robot's. The task list first; then the
      // robot the engine wrote into the message, for a job the list no
      // longer holds (`task_attention`, `task_long_running`).
      const s = alertSubject(row);
      const fromMessage =
        s?.kind === 'task_attention' || s?.kind === 'task_long_running' ? s.robot : null;
      const robot = facts.taskRobot(id) ?? fromMessage;
      // An ops click on this row opens the JOB on Tasks (`navigationTargetFor`),
      // so ops read the job first: a row reading only "Robot NomNom" would
      // sit beside that robot's own alerts, sort in among them, and open
      // something other than what it names. A site click points the
      // Overview at the place, and the hotel reads no task ids.
      const job = audience === 'site' ? null : `${KIND_LABEL.task} ${shortTaskId(id)}`;
      if (robot) {
        const name = splitRobotRef(robot)?.robotName ?? robot;
        const at = withFloor(`${KIND_LABEL.robot} ${name}`, facts.robotFloor(robot));
        return job ? `${job}${SEP}${at}` : at;
      }
      return job ?? TOAST_SOURCES.job;
    }
    default:
      // No one place. Ops read the kind and the id, as the Muted devices
      // list does; a site audience reads the subject line.
      return audience === 'site' ? subject : `${KIND_LABEL[kind] ?? kind} ${id}`;
  }
}
